<template>
	<app-panel
		compact
		:loading="loading"
		loading-overlay
		class="overflow-hidden"
		data-testid="planner">
		<div class="flex h-[800px] flex-col">
			<header
				class="flex flex-none gap-3 items-center justify-between border-b border-gray-200 px-6 py-4">
				<div
					class="relative flex items-center rounded-md bg-white shadow-sm md:items-stretch">
					<button
						type="button"
						:disabled="currentWeek == 0"
						class="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50"
						data-testid="prev-week"
						@click="currentWeek--">
						<span class="sr-only">Previous week</span>
						<ChevronLeftIcon
							class="h-5 w-5"
							:class="{ 'opacity-20 cursor-not-allowed': currentWeek == 0 }"
							aria-hidden="true" />
					</button>
					<button
						v-if="indexedWeeks[currentWeek]"
						type="button"
						class="hidden border-y border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
						data-testid="this-week"
						@click="currentWeek = thisWeek">
						{{ formatDate(indexedWeeks[currentWeek].start) }} -
						{{ formatDate(indexedWeeks[currentWeek].end) }}
					</button>
					<span class="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
					<button
						type="button"
						:disabled="currentWeek == 103"
						class="flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50"
						data-testid="next-week"
						@click="currentWeek++">
						<span class="sr-only">Next week</span>
						<ChevronRightIcon
							class="h-5 w-5"
							:class="{ 'opacity-20 cursor-not-allowed': currentWeek == 103 }"
							aria-hidden="true" />
					</button>
				</div>

				<app-button
					v-if="currentWeek !== thisWeek"
					theme="outline"
					class="mr-auto"
					data-testid="this-week-button"
					@click="currentWeek = 52">
					This Week
				</app-button>

				<div class="flex items-center gap-3">
					<app-button
						theme="outline"
						data-testid="toggle-expand"
						@click="toggleRows(!allExpanded)">
						{{ allExpanded ? 'Close all' : 'Expand all' }}
					</app-button>

					<app-checkbox
						v-model="weekends"
						name="weekends"
						label="Weekends"
						wrapper="ring-1 ring-inset ring-gray-300 rounded-md py-[6px] px-3"
						data-testid="weekends-checkbox" />

					<app-dropdown
						v-model="vehicleModel"
						:options="vehicleModels"
						size="large"
						class="w-56"
						@update:model-value="getJourneys">
						Filter Model

						<template #empty> All Models </template>
					</app-dropdown>
				</div>
			</header>
			<div
				v-if="weekDates.length > 0"
				ref="container"
				class="isolate flex flex-auto flex-col overflow-auto bg-white"
				data-testid="grid">
				<div
					style="width: 165%"
					class="flex max-w-full flex-none flex-col"
					:class="weekends ? 'min-w-[1500px]' : 'min-w-[1050px]'">
					<div
						ref="containerNav"
						data-testid="grid-header"
						class="sticky top-0 z-30 flex-none bg-white shadow ring-1 ring-black ring-opacity-5">
						<div
							class="-mr-px grid divide-x divide-gray-100 border-r border-gray-100 text-sm leading-6 text-gray-500"
							:class="weekends ? 'grid-cols-8' : 'grid-cols-6'">
							<div></div>
							<div
								v-for="(date, i) in weekDates"
								:key="i"
								data-testid="week-day"
								class="flex items-center justify-center py-3">
								<span
									>{{
										new Date(date).toLocaleDateString('en-US', {
											weekday: 'short'
										})
									}}

									<span
										class="inline-flex items-center justify-center font-semibold"
										:class="
											compareDates(new Date(date), new Date())
												? 'bg-mabey-950 font-semibold text-white rounded-full h-7 w-7'
												: 'text-gray-900'
										">
										{{ new Date(date).getDate() }}
									</span>
								</span>
							</div>
						</div>
					</div>
					<div class="flex flex-auto" data-testid="grid-body">
						<div class="grid flex-auto grid-cols-1 grid-rows-1">
							<!-- Vertical lines -->
							<div
								class="col-start-1 col-end-2 row-start-1 grid-rows-1 divide-x divide-gray-100 grid"
								:class="weekends ? 'grid-cols-8' : 'grid-cols-6'">
								<div class="col-start-1 row-span-full" />
								<div class="col-start-2 row-span-full" />
								<div class="col-start-3 row-span-full" />
								<div class="col-start-4 row-span-full" />
								<div class="col-start-5 row-span-full" />
								<div class="col-start-6 row-span-full" />
								<div v-if="weekends" class="col-start-7 row-span-full" />
								<div v-if="weekends" class="col-start-8 row-span-full" />
							</div>

							<!-- Events -->
							<ol
								data-testid="grid-rows"
								class="col-start-1 row-start-1 grid divide-y-[1px]"
								:class="weekends ? 'grid-cols-8' : 'grid-cols-6'"
								style="grid-template-rows: auto">
								<li
									v-for="(vehicle, index) in vehicles"
									:key="index"
									class="relative grid"
									:class="
										weekends
											? 'grid-cols-8 col-span-8'
											: 'grid-cols-6 col-span-6'
									"
									:style="{ 'grid-row': index + 1 }"
									data-testid="grid-row">
									<journey-vehicle :vehicle="vehicle" />

									<div
										v-for="(journeys, d) in vehicle.journeys"
										:key="d"
										:data-vehicle-id="vehicle.registration"
										:data-date="d"
										class="drop-target px-2 py-3"
										data-testid="grid-row-body"
										:class="
											isVehicleUnavailableOnDate(vehicle, String(d))
												? 'bg-red-200 border-x-[0.5px] border-red-100'
												: ''
										">
										<!-- Expanded: show all -->
										<div v-if="vehicle.expanded">
											<journey-badge
												v-for="(journey, j) in journeys"
												:key="j"
												:journey="journey" />

											<app-button
												v-if="journeys.length > 1"
												theme="outline"
												size="small"
												class="w-full"
												data-testid="grid-row-collapse"
												@click="vehicle.expanded = false">
												See less
											</app-button>
										</div>

										<!-- Collapsed: show 1 -->
										<div v-else-if="journeys[0]">
											<journey-badge :journey="journeys[0]" />

											<app-button
												v-if="journeys.length > 1"
												theme="outline"
												size="small"
												class="w-full"
												data-testid="grid-row-expand"
												@click="vehicle.expanded = true">
												+{{ journeys.length - 1 }} more
											</app-button>
										</div>
									</div>
								</li>
							</ol>
						</div>
					</div>
				</div>
			</div>
			<span v-else class="flex justify-center h-full items-center text-gray-400 font-medium">
				No journeys for the selected depots
			</span>
		</div>
	</app-panel>
</template>

<script lang="ts" setup>
	import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid'
	import { computed, onMounted, ref, watch } from 'vue'
	import useAppStore from '@/stores/app'
	import DateService from '@/services/date'
	import type { WeekDate } from '@/data/types/week-date'
	import { compareDates, formatDate, YMD } from '@/utils/date'
	import JourneyService from '@/services/journey'
	import type { Vehicle } from '@/data/schema/vehicle'
	import JourneyBadge from './journey-planner/JourneyBadge.vue'
	import JourneyVehicle from './journey-planner/JourneyVehicle.vue'

	const app = useAppStore()
	const api = new JourneyService()

	const props = defineProps<{
		depots: string[]
	}>()

	const loading = ref(true)
	const weeks = ref<WeekDate[]>([])
	const indexedWeeks = computed(() => {
		return Object.values(weeks.value).reduce(
			(acc, value, index) => {
				acc[index] = value
				return acc
			},
			{} as Record<number, { start: string; end: string; year: number }>
		)
	})
	const weekends = ref(false)
	const weekDates = ref([])
	const currentWeek = ref(52)
	const thisWeek = 52
	const vehicles = ref<Vehicle[]>()

	const vehicleModel = ref('')
	const vehicleModels = ref()

	/**
	 * State monitoring of all vehicle rows
	 */
	const allExpanded = computed(() => {
		if (!vehicles.value) {
			return false
		}

		return vehicles.value.every((obj) => obj.expanded)
	})

	/**
	 * Get working week values from API
	 */
	async function getWeeks() {
		const dateService = new DateService()
		return await dateService.getWorkingWeeks(app.queryDate, weekends.value).then((res) => {
			weeks.value = res.data
		})
	}

	/**
	 * Get vehicle and journey data from API
	 */
	async function getJourneys() {
		loading.value = true

		await api
			.getJourneyPlannerJourneys(props.depots.join('|'), {
				start_date: YMD(new Date(indexedWeeks.value[currentWeek.value].start)),
				end_date: YMD(new Date(indexedWeeks.value[currentWeek.value].end)),
				vehicle_model: vehicleModel.value != '' ? vehicleModel.value : undefined,
				weekends: weekends.value
			})
			.then((res) => {
				vehicles.value = res.data.data.map((obj: Vehicle) => ({
					...obj,
					expanded: false
				}))

				loading.value = false
			})
			.finally(() => {
				weekDates.value =
					vehicles.value.length > 0 ? Object.keys(vehicles.value[0].journeys!) : []
			})
	}

	const isVehicleUnavailableOnDate = (vehicle: Vehicle, date: string): boolean => {
		return vehicle.unavailable?.includes(date) ?? false
	}

	/**
	 * Open or close all vehicle rows
	 * @param open
	 */
	function toggleRows(open: boolean) {
		vehicles.value = vehicles.value!.map((obj) => ({
			...obj,
			expanded: open
		}))
	}

	watch(
		() => currentWeek.value,
		() => {
			getJourneys()
		}
	)

	watch(
		() => weekends.value,
		() => {
			loading.value = true

			getWeeks().then(() => {
				getJourneys()
			})
		}
	)

	watch(
		() => props.depots,
		() => {
			getJourneys()
		},
		{ deep: true }
	)

	onMounted(async () => {
		await getWeeks()
		await getJourneys().then(() => {
			vehicleModels.value = Array.from(
				new Set(vehicles.value?.map((vehicle) => vehicle.model).filter((model) => model))
			).map((model) => ({ label: model, value: model }))
		})
	})
</script>
